
export type AccountType = 'asset' | 'liability' | 'equity' | 'income' | 'expense';

export const ACCOUNT_TYPES: AccountType[] = ['asset', 'liability', 'equity', 'income', 'expense'];

export const TYPE_LABEL: Record<AccountType, string> = {
  asset: 'Asset',
  liability: 'Liability',
  equity: 'Equity',
  income: 'Income',
  expense: 'Expense',
};

export const TYPE_COLORS: Record<AccountType, string> = {
  asset: 'var(--color-primary-light)',
  liability: 'var(--color-warning)',
  equity: 'var(--color-secondary)',
  income: 'var(--color-success)',
  expense: 'var(--color-danger)',
};

/**
 * Returns true if the account type has a normal Credit balance.
 */
export const isCreditNormal = (type: string): boolean => {
  return ['liability', 'equity', 'income'].includes(type.toLowerCase());
};

/**
 * Returns the "Nature" label for an account type.
 */
export const getAccountNature = (type: string): 'DR' | 'CR' => {
  return isCreditNormal(type) ? 'CR' : 'DR';
};

/**
 * Formats a balance for display in accounting format (DR/CR)
 */
export interface AccountingBalance {
  value: number;
  suffix: 'DR' | 'CR';
  isNormal: boolean;
}

export const getAccountingBalance = (amount: number, type: string): AccountingBalance => {
  const normalNature = getAccountNature(type);
  
  if (amount === 0) {
    return { value: 0, suffix: normalNature, isNormal: true };
  }

  // If amount is positive, it means it's on the "Normal" side.
  // In my current logic (GL calculation):
  // Asset/Expense delta = (debit - credit)
  // Liab/Eq/Inc delta = (credit - debit)
  // So a positive balance is always "Normal".
  
  if (amount > 0) {
    return {
      value: amount,
      suffix: normalNature,
      isNormal: true
    };
  } else {
    // If negative, it's the opposite of normal.
    return {
      value: Math.abs(amount),
      suffix: normalNature === 'DR' ? 'CR' : 'DR',
      isNormal: false
    };
  }
};

/**
 * Visual Movement Interpretation
 */
export const getMovementLabel = (type: string, debit: number, credit: number) => {
  const t = type.toLowerCase();
  if (debit > 0) {
    if (t === 'asset') return 'Increase Asset';
    if (t === 'expense') return 'Increase Expense';
    if (t === 'liability') return 'Decrease Liability';
    if (t === 'equity') return 'Decrease Equity';
    if (t === 'income') return 'Decrease Income';
  }
  if (credit > 0) {
    if (t === 'asset') return 'Decrease Asset';
    if (t === 'expense') return 'Decrease Expense';
    if (t === 'liability') return 'Increase Liability';
    if (t === 'equity') return 'Increase Equity';
    if (t === 'income') return 'Increase Income';
  }
  return '';
};
